import {ClaudeCodeIcon} from '@github-ui/custom-icons/ClaudeCodeIcon'
import {CodexIcon} from '@github-ui/custom-icons/CodexIcon'
import {getToolDisplayName, type ToolIcon} from '@github-ui/coding-tools-core/types'
import {CopilotAppOpenActionMenuItem} from '@github-ui/copilot-app-launcher-shared/CopilotAppOpenAction'
import {buildCopilotAppOpenTarget, type CopilotAppTarget} from '@github-ui/copilot-app-launcher-shared/deep-link'
import {getCookie} from '@github-ui/cookies'
import {copyText} from '@github-ui/copy-to-clipboard'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {AgentIcon, TerminalIcon, ToolsIcon, VscodeIcon} from '@primer/octicons-react'
import type {ActionMenuButtonProps} from '@primer/react'
import {ActionList, ActionMenu, Tooltip, Truncate} from '@primer/react'
import type {ReactNode, RefObject} from 'react'
import {useCallback, useMemo} from 'react'

import styles from './CodingAssistantButton.module.css'

export type CodingAssistantButtonProps = {
  url: string
  buttonLabel: string
  promptBuilder: (issueUrl: string) => string
  assistants?: readonly CodingAssistant[]
  size: ActionMenuButtonProps['size']
  variant?: ActionMenuButtonProps['variant']
  iconOnly?: boolean
  showIcon?: boolean
  className?: string
  onAssignToAgent?: () => void
  hideOpenInHeading?: boolean
  anchorRef?: RefObject<HTMLElement | null>
}

function getEnabledTools(): Record<string, boolean> | undefined {
  const cookie = getCookie('preferred_coding_tools')
  if (!cookie) return undefined
  try {
    return JSON.parse(decodeURIComponent(cookie.value)) as Record<string, boolean>
  } catch {
    return undefined
  }
}

const CODING_TOOLS_SETTINGS_PATH = '/settings/coding_tools'

function buildVSCodeAgentUrl(prompt: string): string {
  const doubleEncoded = encodeURIComponent(encodeURIComponent(prompt))
  return `vscode://github.copilot-chat?mode=agent&prompt=${doubleEncoded}`
}

function buildCodexUrl(prompt: string): string {
  const encoded = encodeURIComponent(prompt)
  return `codex://new?prompt=${encoded}`
}

function buildClaudeCodeUrl(prompt: string): string {
  const encoded = encodeURIComponent(prompt)
  return `https://claude.ai/code?prompt=${encoded}`
}

type CodingAssistantBase = {
  key: string
  label: string
  preferenceKey?: string
}

export type UrlCodingAssistant = CodingAssistantBase & {
  type: 'url'
  icon: ToolIcon
  promptBuilder: (prompt: string) => string
  urlBuilder: (prompt: string) => string
}

export type CopilotAppCodingAssistant = CodingAssistantBase & {
  type: 'copilot-app'
  entryPoint: string
  targetBuilder: (prompt: string) => CopilotAppTarget
}

export type CodingAssistant = UrlCodingAssistant | CopilotAppCodingAssistant

type ResolvedUrlAssistant = {
  type: 'url'
  key: string
  label: string
  icon: ToolIcon
  openUrl: string
}

type ResolvedCopilotAppAssistant = {
  type: 'copilot-app'
  key: string
  label: string
  entryPoint: string
  openTarget: string
}

type ResolvedCodingAssistant = ResolvedUrlAssistant | ResolvedCopilotAppAssistant

const canonicalPrompt = (prompt: string) => prompt
const defaultAssistants = [
  {
    type: 'url',
    key: 'copilot_vs_code',
    label: getToolDisplayName('copilot_vs_code'),
    icon: VscodeIcon,
    preferenceKey: 'copilot_vs_code',
    promptBuilder: canonicalPrompt,
    urlBuilder: buildVSCodeAgentUrl,
  },
  {
    type: 'url',
    key: 'codex_desktop_app',
    label: getToolDisplayName('codex_desktop_app'),
    icon: CodexIcon,
    preferenceKey: 'codex_desktop_app',
    promptBuilder: canonicalPrompt,
    urlBuilder: buildCodexUrl,
  },
  {
    type: 'url',
    key: 'claude_code_web',
    label: getToolDisplayName('claude_code_web'),
    icon: ClaudeCodeIcon,
    preferenceKey: 'claude_code_web',
    promptBuilder: canonicalPrompt,
    urlBuilder: buildClaudeCodeUrl,
  },
] satisfies readonly UrlCodingAssistant[]

export function getDefaultCodingAssistants(): readonly UrlCodingAssistant[] {
  return defaultAssistants
}

export function createCopilotAppCodingAssistant({
  entryPoint,
  targetBuilder,
  key = 'github-copilot-app',
  label = 'GitHub Copilot App',
  preferenceKey,
}: {
  entryPoint: string
  targetBuilder: (prompt: string) => CopilotAppTarget
  key?: string
  label?: string
  preferenceKey?: string
}): CopilotAppCodingAssistant {
  return {
    type: 'copilot-app',
    key,
    label,
    preferenceKey,
    entryPoint,
    targetBuilder,
  }
}

export function filterCodingAssistants(
  assistants: readonly CodingAssistant[],
  preferredTools: Record<string, boolean> | undefined,
): CodingAssistant[] {
  if (!preferredTools) return [...assistants]

  return assistants.filter(
    assistant => assistant.preferenceKey === undefined || preferredTools[assistant.preferenceKey] === true,
  )
}

function resolveCodingAssistant(assistant: CodingAssistant, prompt: string): ResolvedCodingAssistant | null {
  switch (assistant.type) {
    case 'copilot-app': {
      const openTarget = buildCopilotAppOpenTarget(assistant.targetBuilder(prompt))
      return openTarget ? {...assistant, openTarget} : null
    }
    case 'url':
      return {
        type: assistant.type,
        key: assistant.key,
        label: assistant.label,
        icon: assistant.icon,
        openUrl: assistant.urlBuilder(assistant.promptBuilder(prompt)),
      }
    default:
      return assertNever(assistant)
  }
}

function AssistantMenuItem({assistant}: {assistant: ResolvedCodingAssistant}) {
  switch (assistant.type) {
    case 'copilot-app':
      return (
        <CopilotAppOpenActionMenuItem
          entryPoint={assistant.entryPoint}
          open={assistant.openTarget}
          label={assistant.label}
        />
      )
    case 'url': {
      const Icon = assistant.icon
      return (
        <ActionList.Item onSelect={() => window.open(assistant.openUrl, '_self')}>
          <ActionList.LeadingVisual>
            <span className={styles.AssistantIconViewport}>
              <Icon />
            </span>
          </ActionList.LeadingVisual>
          {assistant.label}
        </ActionList.Item>
      )
    }
    default:
      return assertNever(assistant)
  }
}

function assertNever(_assistant: never): never {
  throw new Error('Unhandled assistant type')
}

export type CodingAssistantMenuItemsProps = {
  prompt: string
  assistants?: readonly CodingAssistant[]
  showSettingsLink?: boolean
  onAssignToAgent?: () => void
  hideOpenInHeading?: boolean
  /** Additional ActionList items rendered immediately after Copy as prompt. */
  additionalMenuItems?: ReactNode
}

export function CodingAssistantMenuItems({
  prompt,
  assistants = defaultAssistants,
  showSettingsLink,
  onAssignToAgent,
  hideOpenInHeading,
  additionalMenuItems,
}: CodingAssistantMenuItemsProps) {
  const visibleAssistants = filterCodingAssistants(assistants, getEnabledTools())
    .map(assistant => resolveCodingAssistant(assistant, prompt))
    .filter((assistant): assistant is ResolvedCodingAssistant => assistant !== null)

  const handleCopyAsPrompt = useCallback(() => {
    void copyText(prompt)
  }, [prompt])

  return (
    <ActionList>
      <ActionList.Item onSelect={handleCopyAsPrompt}>
        <ActionList.LeadingVisual>
          <TerminalIcon />
        </ActionList.LeadingVisual>
        Copy as prompt
        <ActionList.Description variant="block">
          <Truncate maxWidth={240} title="">
            {prompt}
          </Truncate>
        </ActionList.Description>
      </ActionList.Item>
      {additionalMenuItems}
      {onAssignToAgent && (
        <ActionList.Item onSelect={onAssignToAgent}>
          <ActionList.LeadingVisual>
            <AgentIcon />
          </ActionList.LeadingVisual>
          Cloud agent
        </ActionList.Item>
      )}
      {visibleAssistants.length > 0 && (
        <>
          <ActionList.Divider className={styles.divider} />
          <ActionList.Group>
            {!hideOpenInHeading && <ActionList.GroupHeading>Open in</ActionList.GroupHeading>}
            {visibleAssistants.map(assistant => (
              <AssistantMenuItem key={assistant.key} assistant={assistant} />
            ))}
          </ActionList.Group>
        </>
      )}
      {showSettingsLink && (
        <>
          <ActionList.Divider className={styles.divider} />
          <ActionList.LinkItem href={CODING_TOOLS_SETTINGS_PATH}>
            <ActionList.LeadingVisual>
              <ToolsIcon />
            </ActionList.LeadingVisual>
            Configure coding tools
          </ActionList.LinkItem>
        </>
      )}
    </ActionList>
  )
}

export function CodingAssistantButton({
  url,
  buttonLabel,
  promptBuilder,
  assistants,
  size,
  variant,
  iconOnly = false,
  showIcon = true,
  className,
  onAssignToAgent,
  hideOpenInHeading,
  anchorRef,
}: CodingAssistantButtonProps) {
  const prompt = useMemo(() => promptBuilder(url), [promptBuilder, url])
  const showSettingsLink = isFeatureEnabled('coding_tool_settings')

  return (
    <ActionMenu anchorRef={anchorRef}>
      {iconOnly ? (
        <Tooltip text={buttonLabel} type="label">
          <ActionMenu.Button className={className} leadingVisual={TerminalIcon} size={size} variant={variant} />
        </Tooltip>
      ) : (
        <ActionMenu.Button
          aria-label={buttonLabel}
          className={className}
          leadingVisual={showIcon ? TerminalIcon : undefined}
          size={size}
          variant={variant}
        >
          {buttonLabel}
        </ActionMenu.Button>
      )}
      <ActionMenu.Overlay className={styles.overlay}>
        <CodingAssistantMenuItems
          prompt={prompt}
          assistants={assistants}
          showSettingsLink={showSettingsLink}
          onAssignToAgent={onAssignToAgent}
          hideOpenInHeading={hideOpenInHeading}
        />
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
